const isValidOuterOrigin = (candidate: unknown): candidate is string => {
	// Must be a genuinely different (outer) origin — never echo this frame's own origin.
	if (typeof candidate !== 'string' || candidate === window.location.origin) {
		return false;
	}
	try {
		const url = new URL(candidate);
		// Require a canonical https origin (scheme + host [+ port] and nothing else). Comparing
		// against `url.origin` rejects any path, query, whitespace, or injected CSP tokens, so a
		// malformed / attacker-shaped value can never reach the macro's `frame-ancestors` directive.
		return url.protocol === 'https:' && url.origin === candidate;
	} catch {
		return false;
	}
};

// Server-injected origin, present only on verified custom-domain SSR renders.
const getSsrEmbeddingOrigin = (): string | undefined => {
	const origin = (window as unknown as { __SSR_EMBEDDING_ORIGIN__?: unknown })
		.__SSR_EMBEDDING_ORIGIN__;
	return isValidOuterOrigin(origin) ? origin : undefined;
};

// Embedded-confluence connect context (`options.hostOrigin`) serialised into `window.name`.
// This is specific for connect and a temp solution in case
// getSsrEmbeddingOrigin does not work as expected.
const getConnectContextEmbeddingOrigin = (): string | undefined => {
	try {
		const parsed = JSON.parse(window.name) as { options?: { hostOrigin?: unknown } };
		const hostOrigin = parsed?.options?.hostOrigin;
		return isValidOuterOrigin(hostOrigin) ? hostOrigin : undefined;
	} catch {
		return undefined;
	}
};

/**
 * Resolves the trusted cross-domain embedding origin (e.g. `"https://refined.site"`) when this
 * Confluence page is embedded cross-domain, or `undefined` otherwise (so a truthy result doubles
 * as the "embedded cross-domain" signal). This is about the outer origin framing the page — not
 * Atlassian's Custom Domains feature. The only source is `window.__SSR_EMBEDDING_ORIGIN__` —
 * injected by Bifrost SSR from the value FrameAncestorsResolver derives from the verified `Referer`
 * and appends to Confluence's own `frame-ancestors` CSP — so the Forge macro's CSP can never be
 * looser and we avoid unreliable client APIs. Returns `undefined` when not SSR'd with a verified
 * cross-domain origin or when the origin is same-origin with this page.
 */
export const getCrossDomainEmbeddingOrigin = (): string | undefined =>
	getSsrEmbeddingOrigin() ?? getConnectContextEmbeddingOrigin();
